import React from "react";
import TemplateCard from "./TemplateCard";
import { AcademicCapIcon, BeakerIcon } from "@heroicons/react/24/outline";

/**
 * TemplateGrid Component
 * Displays templates in a responsive grid layout with sections
 *
 * @param {Array} templates - Array of template objects
 * @param {Object} scores - Object containing scores for each template by template ID
 * @param {function} onTemplateClick - Handler when template card is clicked
 */
const TemplateGrid = ({ templates = [], scores = {}, onTemplateClick }) => {
  // Separate templates by type
  const coreTemplates = templates.filter((t) => t.type === "core");
  const supplementaryTemplates = templates.filter((t) => t.type === "supplementary");

  /**
   * Calculate completion status and metrics for a template
   */
  const getTemplateMetrics = (template) => {
    const scoreData = scores[template._id] || {};
    const templateScores = scoreData.scores || {};
    const items = template.items || [];
    const totalItems = items.length;

    console.log(`[TemplateGrid] Calculating metrics for template: ${template.name}`);
    console.log(`[TemplateGrid] Score data from backend:`, scoreData);
    console.log(`[TemplateGrid] Template scores:`, templateScores);

    // Use backend-calculated values if available (they already exclude N/A items)
    if (scoreData.metadata && typeof scoreData.metadata.totalScore === 'number') {
      console.log('[TemplateGrid] Using backend-calculated metrics');

      // Use backend-calculated values directly - backend already handles N/A items correctly
      const backendTotalScore = scoreData.metadata.totalScore || 0;
      const backendMaxScore = scoreData.metadata.maxPossibleScore || 0;
      const backendItemsCompleted = scoreData.metadata.itemsCompleted || 0;
      const backendItemsNotApplicable = scoreData.metadata.itemsNotApplicable || 0;
      const backendCompletionStatus = scoreData.metadata.completionStatus || "not_started";

      console.log(`[TemplateGrid] Final metrics (from backend):`, {
        itemsCompleted: backendItemsCompleted,
        itemsTotal: totalItems,
        itemsNotApplicable: backendItemsNotApplicable,
        totalScore: backendTotalScore,
        maxPossibleScore: backendMaxScore,
        completionStatus: backendCompletionStatus
      });

      return {
        itemsCompleted: backendItemsCompleted,
        itemsTotal: totalItems,
        itemsNotApplicable: backendItemsNotApplicable,
        totalScore: backendTotalScore,
        maxPossibleScore: backendMaxScore,
        completionStatus: backendCompletionStatus,
      };
    }

    // Fallback: Calculate from scratch if backend values not available
    console.log('[TemplateGrid] Backend metrics not available, calculating from itemScores');
    let itemsCompleted = 0;
    let itemsNotApplicable = 0;
    let totalScore = 0;
    let maxPossibleScore = 0;

    items.forEach((item) => {
      // Check if this item has been scored (look up by item._id or item.name)
      const itemScore = templateScores[item._id] || templateScores[item.name];
      const isNa = itemScore === null; // null indicates N/A
      const hasScore = itemScore !== undefined && itemScore !== null && itemScore !== "";

      console.log(`[TemplateGrid] Item: ${item.name}, score: ${itemScore}, isNa: ${isNa}, hasScore: ${hasScore}`);

      // Count N/A items (they count as completed since they've been handled)
      if (isNa) {
        itemsNotApplicable++;
        itemsCompleted++; // N/A items count as completed
      } else {
        // Only include in score calculations if NOT marked as N/A
        // Use item.score as the max score for this item (as per backend schema)
        const itemMaxScore = item.score || 0;
        maxPossibleScore += itemMaxScore;

        // If item has a score, count it as completed
        if (hasScore) {
          itemsCompleted++;
          totalScore += parseFloat(itemScore) || 0;
        }
      }
    });

    // All items count toward total (including N/A items)
    const applicableItemsTotal = totalItems;

    // Determine completion status based on all items (including N/A)
    let completionStatus = "not_started";
    if (itemsCompleted === 0) {
      completionStatus = "not_started";
    } else if (itemsCompleted < applicableItemsTotal) {
      completionStatus = "partial";
    } else {
      completionStatus = "complete";
    }

    console.log(`[TemplateGrid] Final metrics:`, {
      itemsCompleted,
      applicableItemsTotal,
      itemsNotApplicable,
      totalScore,
      maxPossibleScore,
      completionStatus
    });

    return {
      itemsCompleted,
      itemsTotal: applicableItemsTotal, // Only count applicable items
      itemsNotApplicable,
      totalScore,
      maxPossibleScore,
      completionStatus,
    };
  };

  /**
   * Render a section of templates
   */
  const renderTemplateSection = (sectionTemplates, title, icon, description) => {
    if (sectionTemplates.length === 0) {
      return null;
    }

    const Icon = icon;

    return (
      <div className="mb-8">
        {/* Section Header */}
        <div className="mb-4 border-b-2 border-gray-200 pb-3">
          <div className="flex items-center space-x-2">
            <Icon className="h-6 w-6 text-gray-700" />
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
          </div>
          {description && (
            <p className="text-sm text-gray-600 mt-1 ml-8">{description}</p>
          )}
        </div>

        {/* Templates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sectionTemplates.map((template) => {
            const metrics = getTemplateMetrics(template);

            return (
              <TemplateCard
                key={template._id}
                template={template}
                completionStatus={metrics.completionStatus}
                onClick={() => onTemplateClick && onTemplateClick(template)}
                itemsCompleted={metrics.itemsCompleted}
                itemsTotal={metrics.itemsTotal}
                itemsNotApplicable={metrics.itemsNotApplicable}
                totalScore={metrics.totalScore}
                maxPossibleScore={metrics.maxPossibleScore}
              />
            );
          })}
        </div>
      </div>
    );
  };

  // Show empty state if no templates
  if (templates.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
          <AcademicCapIcon className="h-8 w-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          No Templates Available
        </h3>
        <p className="text-sm text-gray-600">
          There are no scoring templates assigned to this programme yet.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Core Templates Section */}
      {renderTemplateSection(
        coreTemplates,
        "Core Templates",
        AcademicCapIcon,
        "Essential scoring areas that must be completed"
      )}

      {/* Supplementary Templates Section */}
      {renderTemplateSection(
        supplementaryTemplates,
        "Supplementary Templates",
        BeakerIcon,
        "Additional scoring areas that provide supplementary information"
      )}
    </div>
  );
};

export default TemplateGrid;
